import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import {
  QueryObserverResult,
  RefetchOptions,
  RefetchQueryFilters,
  useQuery,
} from '@tanstack/react-query'
import { DEFAULT_TABLE_ITEMS_PER_PAGE } from '../../constants'
import { IPaginatedData } from 'models/shared.models'
import { selectErrorDialog } from 'lib/helpers/errors.helpers'
import useDialog from 'hooks/Shared/useDialog'
import {
  IApiError,
  IPaginatedQueryRequest,
} from 'services/Shared/shared.models'

interface IUseDataGridServerPaginationProps<TData, TFilters> {
  enabled?: boolean
  errorMessage?: string
  fetchFunction: (
    params: IPaginatedQueryRequest<TFilters>
  ) => Promise<IPaginatedData<TData>>
  activeWorkspace?: number
  filters?: TFilters
  isInfiniteScroll?: boolean
  pageSize?: number
  withFilters?: boolean
  refreshOn?: unknown
  errorCallback?: (error: IApiError) => void
  successCallback?: (data: TData[]) => void
}

export interface IUseDataGridServerPagination<TData> {
  ref: MutableRefObject<HTMLDivElement | null>
  isError: boolean

  isLoading: boolean
  rows: TData[]
  rowCount: number
  paginationMode: 'server'

  refetch: <TPageData>(
    options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
  ) => Promise<QueryObserverResult<IPaginatedData<TData>, IApiError>>
  onPageChange: (newPage: number) => void
  itemsPerPage?: number
  pagination?: true
  page?: number
}

const useDataGridServerPagination = <TData, TFilters>({
  activeWorkspace,
  enabled = true,
  errorMessage,
  filters,
  isInfiniteScroll = false,
  pageSize,
  withFilters,
  errorCallback,
  fetchFunction,
  successCallback,
  refreshOn,
}: IUseDataGridServerPaginationProps<
  TData,
  TFilters
>): IUseDataGridServerPagination<TData> => {
  const dialog = useDialog()

  const [page, setPage] = useState(0)
  const [tableData, setTableData] = useState<TData[]>([])

  const dynamicPageSize = pageSize ?? DEFAULT_TABLE_ITEMS_PER_PAGE

  const tableRef = useRef<HTMLDivElement | null>(null)

  const { data, isError, isFetching, isLoading, refetch } = useQuery<
    IPaginatedData<TData>,
    IApiError
  >(
    [
      'dataGridServerPagination',
      activeWorkspace,
      page,
      filters,
      refreshOn,
      fetchFunction,
    ],
    () => {
      if (withFilters && filters) {
        return activeWorkspace
          ? fetchFunction({
              activeWorkspace,
              page,
              pageSize: dynamicPageSize,
              filters,
            })
          : fetchFunction({ page, pageSize: dynamicPageSize, filters })
      }
      return fetchFunction({ activeWorkspace, page, pageSize: dynamicPageSize })
    },
    {
      enabled,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      onSuccess: data => {
        isInfiniteScroll
          ? setTableData(prevData => [...prevData, ...data.list])
          : setTableData(data.list)

        successCallback && successCallback(data.list)
      },
      onError: async (error: IApiError) => {
        setTableData([])

        const predefinedErrorModalConfig = selectErrorDialog(error)

        if (errorMessage) {
          if (
            predefinedErrorModalConfig &&
            predefinedErrorModalConfig.variant
          ) {
            await dialog[predefinedErrorModalConfig.variant](
              predefinedErrorModalConfig
            )
            return
          }

          if (error.status !== 404) {
            dialog.danger({
              subtitle: errorMessage,
              primaryButtonText: 'Reintentar',
              secondaryButtonText: 'Cancelar',
              onPrimaryClick: refetch,
              onSecondaryClick: () => {
                errorCallback && errorCallback(error)
              },
            })
          }
        }
      },
    }
  )

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage)
  }, [])

  useEffect(() => {
    const element =
      tableRef.current?.getElementsByClassName('MuiDataGrid-window')[0]

    const onScroll = () => {
      if (isInfiniteScroll && element) {
        const hasVerticalScroll = element.scrollHeight > element.clientHeight
        const isScrolledToBottom =
          hasVerticalScroll &&
          element.scrollHeight - element.scrollTop === element.clientHeight

        if (isScrolledToBottom) {
          data?.totalPages &&
            page + 1 < data.totalPages &&
            handlePageChange(page + 1)
        }
      }
    }

    isInfiniteScroll && element?.addEventListener('scroll', onScroll)

    return () => {
      element?.removeEventListener('scroll', onScroll)
    }
    /* I'm disabling the rule ONLY to add the element ref to the dependencies without causing a warning,
    since without it the event listener stops working upon unmounting/mounting the component */

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    data?.totalPages,
    handlePageChange,
    isInfiniteScroll,
    page,
    tableRef.current,
  ])

  return {
    ref: tableRef,
    isError,
    isLoading: isLoading || isFetching,
    rows: tableData,
    rowCount: data?.total ?? 0,
    page: !isInfiniteScroll ? page : undefined,
    itemsPerPage: isInfiniteScroll ? tableData.length : dynamicPageSize,
    pagination: !isInfiniteScroll ? true : undefined,
    paginationMode: 'server',
    onPageChange: handlePageChange,
    refetch,
  }
}

export default useDataGridServerPagination
